import React, { type FC, useEffect, useState, useRef } from "react";
import { useRouter } from "next/router";
import { getIdentityType, userIdenityType } from "@/utility/identityType";
import { saveSessionStorage } from "@/utility/saveSessionStorage";
import { getPortal } from "@/utility/getPortal";
import { getPlatform, getDevice, getOS } from "@/utility/userAgent";
import { OTPActivityVerifiedUser, OTPSessionID } from "@/constants/index";
import { CustomErrors } from "@/constants/customErrorStrings";
import { customErrorMessages } from "@/constants/customErrorMessages";
import Head from "next/head";
import Layout from "layouts";
import Alert from "@/components/Alert";
import ReCAPTCHA from "react-google-recaptcha";

import {
  retypePasswordValidation,
  phoneNumberMinLengthValidation,
  phoneNumberMaxLengthValidation,
  emailValidation,
  firstNameMaxLengthValidation,
  lastNameMaxLengthValidation,
  passwordMinLengthValidation,
  passwordUpperLowerValidation,
  passwordSymbolValidation,
} from "@/constants/validationMessages";

import { Auth, I18n } from "aws-amplify";
import { GetPageLink } from "@/utility/getLinkPage";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Divider,
  Flex,
  Spacer,
  useBoolean,
  useDisclosure,
} from "@chakra-ui/react";
import {
  ButtonPrimary,
  Checkbox,
  InputPassword,
  InputText,
} from "@/components/Form";
import ChecklistItem from "./ChecklistItem";
import { Body } from "@/components/Typography";
import { Link } from "@/components/Navigation";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import { colors } from "@/styles/theme/constants";
import ModalHeader from "@/components/Modal/ModalHeader";
import { Headset } from "@/components/Icon";

// password validation
const MIN_PASS_CHAR_VLD = 8;
const PASS_CHAR_VLD = /^(?=.*[a-z])(?=.*[A-Z]).*$/;
const PASS_NUM_VLD = /(?=.*[0-9]).*$/;
const PASS_SPCL_VLD = /(?=[A-Za-z0-9!@#$%^&*()]+$)^(?=.*[!@#$%^&*()]).*$/;

// email format validation
const EMAIL_VLD = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

// full name validation
const MAX_FIRSTNAME_CHAR_VLD = 128;
const MAX_LASTNAME_CHAR_VLD = 127;

const { v4: uuidv4 } = require("uuid");

const Register: FC = () => {
  const [identity, setIdentity] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");

  const [typingIdentityTimeout, setTypingIdentityTimeout] = useState(0);
  const [typingFirstNameTimeout, setTypingFirstNameTimeout] = useState(0);
  const [typingLastNameTimeout, setTypingLastNameTimeout] = useState(0);
  const [typingPassTimeout, setTypingPassTimeout] = useState(0);
  const [typingRetypePassTimeout, setTypingRetypePassTimeout] = useState(0);

  const [hidePasswordChecklist, setHidePasswordChecklist] = useState(true);

  const [validationIdentityError, setValidationIdentityError] = useState("");
  const [errors, setErrors] = useState(new Map());
  const [alertMsg, setAlertMsg] = useState("");

  const [checklistPasswordErrors, setChecklistPasswordErrors] = useState(
    new Map()
  );

  const [isButtonSubmitDisabled, setIsButtonSubmitDisabled] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);

  const [isTermsApproved, setIsTermsApproved] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();

  const router = useRouter();

  useEffect(() => {
    setIsButtonSubmitDisabled(true);

    // button disable when validation error exist on form inputs
    if (
      validationIdentityError !== "" ||
      errors.get("firstName") !== "" ||
      errors.get("lastName") !== "" ||
      errors.get("password") !== "" ||
      errors.get("retypePassword") !== "" ||
      identity == "" ||
      password == "" ||
      retypePassword == "" ||
      retypePassword != password ||
      firstName == "" ||
      lastName == ""
    ) {
      setIsButtonSubmitDisabled(true);
    } else {
      // button enable when all inputs are no errors
      setIsButtonSubmitDisabled(false);
    }
  }, [
    // repeat checking if state has change
    validationIdentityError,
    errors,
    identity,
    firstName,
    lastName,
    password,
    retypePassword,
  ]);

  const handleValidateIdentity = (
    e: React.FormEvent<HTMLInputElement>,
    delay = 0
  ) => {
    const inputValue = (e.target as HTMLInputElement).value
      ?.toLowerCase()
      .trim();
    let userIdentity = (e.target as HTMLInputElement).value
      ?.toLowerCase()
      .trim();

    setIdentity(userIdentity);
    setValidationIdentityError("");
    setAlertMsg("");

    if (typingIdentityTimeout) {
      clearTimeout(typingIdentityTimeout);
    }

    if (getIdentityType(userIdentity) == userIdenityType.PHONE_NUMBER) {
      if (userIdentity.substring(0, 1) == "0") {
        userIdentity = "+62" + userIdentity.substring(1);
      } else if (userIdentity.substring(0, 2) == "62") {
        userIdentity = "+" + userIdentity;
      }

      setIdentity(userIdentity);

      setTypingIdentityTimeout(
        window.setTimeout(function () {
          if (inputValue == "") {
            setValidationIdentityError("");
            setIsButtonSubmitDisabled(true);
          } else if (inputValue.length < 5) {
            setValidationIdentityError(phoneNumberMinLengthValidation);
          } else if (inputValue.length > 20) {
            setValidationIdentityError(phoneNumberMaxLengthValidation);
          } else {
            setValidationIdentityError("");
          }
        }, delay)
      );
    } else {
      setTypingIdentityTimeout(
        window.setTimeout(function () {
          if (inputValue == "") {
            setValidationIdentityError("");
            setIsButtonSubmitDisabled(true);
          } else if (
            inputValue.includes("@") == false ||
            inputValue.includes(".") == false
          ) {
            setValidationIdentityError(emailValidation);
          } else if (EMAIL_VLD.test(inputValue) === false) {
            setValidationIdentityError(emailValidation);
          } else {
            setValidationIdentityError("");
          }
        }, delay)
      );
    }
  };

  // handle temporary disable phone number register
  const handleValidateIdentityEmail = (
    e: React.FormEvent<HTMLInputElement>,
    delay = 0
  ) => {
    const inputValue = (e.target as HTMLInputElement).value
      ?.toLowerCase()
      .trim();
    let userIdentity = (e.target as HTMLInputElement).value
      ?.toLowerCase()
      .trim();

    setIdentity(userIdentity);
    setValidationIdentityError("");
    setAlertMsg("");

    if (typingIdentityTimeout) {
      clearTimeout(typingIdentityTimeout);
    }

    setTypingIdentityTimeout(
      window.setTimeout(function () {
        if (inputValue == "") {
          setValidationIdentityError("");
          setIsButtonSubmitDisabled(true);
        } else if (
          inputValue.includes("@") == false ||
          inputValue.includes(".") == false
        ) {
          setValidationIdentityError(emailValidation);
        } else if (EMAIL_VLD.test(inputValue) === false) {
          setValidationIdentityError(emailValidation);
        } else {
          setValidationIdentityError("");
        }
      }, delay)
    );
  };

  const handleValidateFirstName = (
    e: React.FormEvent<HTMLInputElement>,
    delay = 0
  ) => {
    let val = (e.target as HTMLInputElement).value?.trim();
    setFirstName(val);
    setErrors((map) => new Map(map.set("firstName", "")));

    if (typingFirstNameTimeout) {
      clearTimeout(typingFirstNameTimeout);
    }

    setTypingFirstNameTimeout(
      window.setTimeout(function () {
        if (val.length > MAX_FIRSTNAME_CHAR_VLD) {
          setErrors(
            (map) => new Map(map.set("firstName", firstNameMaxLengthValidation))
          );
        }
      }, delay)
    );
  };

  const handleValidateLastName = (
    e: React.FormEvent<HTMLInputElement>,
    delay = 0
  ) => {
    let val = (e.target as HTMLInputElement).value?.trim();
    setLastName(val);
    setErrors((map) => new Map(map.set("lastName", "")));

    if (typingLastNameTimeout) {
      clearTimeout(typingLastNameTimeout);
    }

    setTypingLastNameTimeout(
      window.setTimeout(function () {
        if (val.length > MAX_LASTNAME_CHAR_VLD) {
          setErrors(
            (map) => new Map(map.set("lastName", lastNameMaxLengthValidation))
          );
        }
      }, delay)
    );
  };

  const handleValidatePassword = (
    e: React.FormEvent<HTMLInputElement>,
    delay = 0
  ) => {
    let value = (e.target as HTMLInputElement).value;
    let input = (e.target as HTMLInputElement).name;

    if (input == "retype-password") {
      setRetypePassword(value);
      setErrors((map) => new Map(map.set("retypePassword", "")));

      if (typingRetypePassTimeout) {
        clearTimeout(typingRetypePassTimeout);
      }

      setTypingRetypePassTimeout(
        window.setTimeout(() => {
          if (password != value) {
            setErrors(
              (map) =>
                new Map(map.set("retypePassword", retypePasswordValidation))
            );
          } else {
            setErrors((map) => new Map(map.set("retypePassword", "")));
          }
        }, delay)
      );
    } else if (input == "password") {
      setPassword(value);
      setErrors((map) => new Map(map.set("password", "")));

      if (typingPassTimeout) {
        clearTimeout(typingPassTimeout);
      }
      setTypingPassTimeout(
        window.setTimeout(() => {
          //checking form input validation
          if (value == "") {
            setErrors((map) => new Map(map.set("password", "")));
            setIsButtonSubmitDisabled(true);
          } else if (value.length < MIN_PASS_CHAR_VLD) {
            setErrors(
              (map) => new Map(map.set("password", passwordMinLengthValidation))
            );
          } else if (PASS_CHAR_VLD.test(value) === false) {
            setErrors(
              (map) =>
                new Map(map.set("password", passwordUpperLowerValidation))
            );
          } else if (
            PASS_NUM_VLD.test(value) === false ||
            PASS_SPCL_VLD.test(value) === false
          ) {
            setErrors(
              (map) => new Map(map.set("password", passwordSymbolValidation))
            );
          } else {
            setErrors((map) => new Map(map.set("password", "")));
          }

          // checking checklist validation
          if (value.length < MIN_PASS_CHAR_VLD) {
            setChecklistPasswordErrors((map) => new Map(map.set("min", true)));
          } else {
            setChecklistPasswordErrors((map) => new Map(map.set("min", false)));
          }

          if (PASS_CHAR_VLD.test(value) === false) {
            setChecklistPasswordErrors(
              (map) => new Map(map.set("alphabet", true))
            );
          } else {
            setChecklistPasswordErrors(
              (map) => new Map(map.set("alphabet", false))
            );
          }

          if (
            PASS_NUM_VLD.test(value) === false ||
            PASS_SPCL_VLD.test(value) === false
          ) {
            setChecklistPasswordErrors(
              (map) => new Map(map.set("symbolNumeric", true))
            );
          } else {
            setChecklistPasswordErrors(
              (map) => new Map(map.set("symbolNumeric", false))
            );
          }

          //checking retype password validation
          if (retypePassword.length != 0 && retypePassword != value) {
            setErrors(
              (map) =>
                new Map(map.set("retypePassword", retypePasswordValidation))
            );
          } else {
            setErrors((map) => new Map(map.set("retypePassword", "")));
          }
        }, delay)
      );
    }
  };

  const handleShowChecklist = () => {
    if (hidePasswordChecklist) {
      setHidePasswordChecklist(false);
      setChecklistPasswordErrors((map) => new Map(map.set("min", true)));
      setChecklistPasswordErrors((map) => new Map(map.set("alphabet", true)));
      setChecklistPasswordErrors(
        (map) => new Map(map.set("symbolNumeric", true))
      );
    }
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
    recaptchaToken: string
  ) => {
    e.preventDefault();
    setAlertMsg("");

    const data = e.target as typeof e.target & {
      password: { value: string };
      first_name: { value: string };
      last_name: { value: string };
    };

    try {
      setLoadingButton(true);

      const clientMetaData = {
        portal: getPortal(window.location.hostname),
        platform: getPlatform(),
        user_agent: navigator.userAgent,
        device: getDevice(),
        operating_system: getOS(),
        ip_address: "",
        recaptchaToken: recaptchaToken || "",
      };

      let username = uuidv4();

      let attributes: { [key: string]: string } = {
        email: identity,
        "custom:first_name": data.first_name.value,
        "custom:last_name": data.last_name.value,
        name: `${data.first_name.value} ${data.last_name.value}`,
      };

      if (getIdentityType(identity) == userIdenityType.PHONE_NUMBER) {
        attributes = {
          phone_number: identity,
          "custom:first_name": data.first_name.value,
          "custom:last_name": data.last_name.value,
          name: `${data.first_name.value} ${data.last_name.value}`,
        };
      }

      // call signUp API - send verification code to user and redirect to verification page if the code was successfully sent
      const { user, userConfirmed } = await Auth.signUp({
        username: username,
        password: data.password.value,
        attributes: attributes,
        clientMetadata: clientMetaData,
      });

      // handle verification process when user successfully registered
      if (user && !userConfirmed) {
        saveSessionStorage(OTPSessionID, {
          user_identity: identity,
          username: username,
          activity_otp: OTPActivityVerifiedUser,
        });

        let pathname = GetPageLink("/verification", router.query);

        router.push(pathname);
      }
    } catch (err: any) {
      setLoadingButton(false);
      let errMsg = CustomErrors.DEFAULT_MSG;

      if (err.message) {
        if (
          err.message == CustomErrors.USER_ALREADY_EXIST_REGISTER_IDNACCOUNT
        ) {
          if (getIdentityType(identity) == userIdenityType.PHONE_NUMBER) {
            errMsg = I18n.get(CustomErrors.PHONE_NUMBER_ALREADY_EXIST_REGISTER);
          } else {
            errMsg = I18n.get(CustomErrors.EMAIL_ALREADY_EXIST_REGISTER);
          }
        } else {
          if (customErrorMessages.hasOwnProperty(err.message)) {
            errMsg = I18n.get(err.message);
          }
        }
      }

      setAlertMsg(errMsg);
      setPassword("");
      setRetypePassword("");

      setChecklistPasswordErrors((map) => new Map(map.set("min", true)));
      setChecklistPasswordErrors((map) => new Map(map.set("alphabet", true)));
      setChecklistPasswordErrors(
        (map) => new Map(map.set("symbolNumeric", true))
      );
    }
  };

  const [tokenRecaptcha, setTokenRecaptcha] = useState("");
  const [isRecaptchaChecked, setIsRecaptchaChecked] = useState(false);
  let recaptchaRef = useRef<any>(null);

  const onVerifyRecaptcha = (token: any) => {
    setTokenRecaptcha(token);
    setIsRecaptchaChecked(true);
  };

  const onExpiredRecaptcha = () => {
    setTokenRecaptcha("");
    setIsRecaptchaChecked(false);
  };

  const handleAgreePolicy = () => {
    setIsTermsApproved.on();
    onClose();
  };

  // reset recaptcha widget when error submit on registration
  useEffect(() => {
    if (alertMsg) {
      onExpiredRecaptcha();
      recaptchaRef?.current?.reset();
    }
  }, [alertMsg]);

  const accordionContent = [
    {
      title: "Pengumpulan Data Pribadi",
      content: (
        <>
          Kami mengumpulkan data pribadi saat Anda membuat akun IDN Connect,
          termasuk namun tidak terbatas pada:
          <ol style={{ marginLeft: "1rem", marginTop: "0.5rem" }}>
            <li>1. Nama lengkap</li>
            <li>2. Alamat email</li>
            <li>3. Nomor telepon</li>
            <li>4. Tanggal lahir</li>
            <li>5. Data aktivitas Anda dalam ekosistem IDN</li>
          </ol>
          <p style={{ marginTop: "0.75rem" }}>
            Dengan membuat akun, Anda menyatakan bahwa data yang Anda berikan
            adalah benar dan milik pribadi, bukan milik orang lain. Penggunaan
            identitas palsu atau milik pihak lain tanpa izin melanggar kebijakan
            kami dan dapat dikenakan sanksi
          </p>
        </>
      ),
    },
    {
      title: "Penggunaan Data untuk Promosi dan Marketing",
      content: (
        <>
          Dengan menggunakan layanan IDN Connect, Anda menyetujui bahwa data
          diri dan aktivitas Anda akan dicatat dan digunakan untuk:
          <ol style={{ marginLeft: "1rem", marginTop: "0.5rem" }}>
            <li>
              1. Mengirimkan informasi promosi, penawaran khusus, atau berita
              terbaru seputar produk dan layanan IDN
            </li>
            <li>
              2. Memberikan konten dan rekomendasi yang dipersonalisasi sesuai
              minat Anda
            </li>
            <li>
              3. Melakukan analisis perilaku pengguna untuk meningkatkan
              kualitas layanan
            </li>
            <li>
              4. Kami tidak akan menjual data pribadi Anda kepada pihak ketiga.
              Data hanya akan digunakan untuk kebutuhan internal dan afiliasi
              terpercaya kami yang mendukung layanan IDN Connect.
            </li>
          </ol>
        </>
      ),
    },
    {
      title: "Hak Pengguna",
      content: (
        <>
          Anda memiliki hak untuk:
          <ol style={{ marginLeft: "1rem", marginTop: "0.5rem" }}>
            <li>
              1. Mengakses dan memperbarui data pribadi Anda kapan saja melalui
              halaman akun
            </li>
            <li>
              2. Menarik persetujuan Anda terkait penggunaan data untuk promosi
              (opt-out) melalui pengaturan akun atau tautan berhenti
              berlangganan
            </li>
            <li>
              3. Menghapus akun dan seluruh data pribadi Anda dari sistem kami,
              sesuai prosedur yang berlaku
            </li>
          </ol>
        </>
      ),
    },
    {
      title: "Keamanan dan Penyimpanan Data",
      content: (
        <>
          Kami menerapkan langkah-langkah teknis dan organisasi untuk melindungi
          data Anda dari akses yang tidak sah, perubahan, atau penyalahgunaan.
          Data Anda akan disimpan selama akun Anda aktif atau selama dibutuhkan
          untuk tujuan yang dijelaskan dalam Kebijakan Privasi ini.
        </>
      ),
    },
    {
      title: "Perubahan Kebijakan Privasi",
      content: (
        <>
          Kebijakan ini dapat diperbarui dari waktu ke waktu untuk mencerminkan
          perubahan pada praktik kami atau peraturan yang berlaku. Kami akan
          memberitahu Anda melalui email atau notifikasi pada aplikasi jika
          terdapat perubahan signifikan.
        </>
      ),
    },
  ];

  return (
    <Layout backUrl="/">
      <Head>
        <title>Daftar Akun Baru | Gabung dengan IDN Connect Sekarang</title>
        <meta
          name="description"
          content="Buat akun IDN Connect dan nikmati berbagai layanan yang tesedia. Daftar sekarang dengan mudah!"
        />
      </Head>
      <form
        data-testid="form_register"
        autoComplete="off"
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          handleSubmit(e, tokenRecaptcha);
          onExpiredRecaptcha();
          // recaptchaRef?.current?.reset();
        }}
      >
        {alertMsg ? <Alert text={alertMsg} /> : null}
        <Flex
          flexDirection={{ base: "column", md: "row" }}
          gap={{ base: 0, md: "8px" }}
        >
          <InputText
            data-testid="input_firstName"
            name="first_name"
            label="Nama Depan"
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              handleValidateFirstName(e, 2000)
            }
            onBlur={(e: React.FormEvent<HTMLInputElement>) =>
              handleValidateFirstName(e)
            }
            errorMessage={errors.get("firstName")}
            formControlProps={{
              mb: "8px",
            }}
          />
          <InputText
            data-testid="input_lastName"
            name="last_name"
            label="Nama Belakang"
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              handleValidateLastName(e, 2000)
            }
            onBlur={(e: React.FormEvent<HTMLInputElement>) =>
              handleValidateLastName(e)
            }
            errorMessage={errors.get("lastName")}
            formControlProps={{
              mb: "8px",
            }}
          />
        </Flex>
        <InputText
          data-testid="input_email"
          name="user_identity"
          label="Email"
          errorMessage={validationIdentityError}
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            handleValidateIdentityEmail(e, 2000)
          }
          onBlur={(e: React.FormEvent<HTMLInputElement>) =>
            handleValidateIdentityEmail(e)
          }
          formControlProps={{
            mb: "8px",
          }}
          autoComplete="off"
        />
        <InputPassword
          data-testid="input_password"
          label="Password"
          name="password"
          value={password}
          onChange={(e: React.FormEvent<HTMLInputElement>) => {
            handleValidatePassword(e, 2000);
            handleShowChecklist();
          }}
          onBlur={(e: React.FormEvent<HTMLInputElement>) => {
            handleValidatePassword(e);
          }}
          errorMessage={errors.get("password")}
          autoComplete="new-password"
        />
        <Spacer mb="8px" />
        {!hidePasswordChecklist && (
          <Box data-testid="list_passwordValidation" my="8px">
            <ChecklistItem
              text="Minimal 8 karakter"
              isError={checklistPasswordErrors.get("min") ? true : false}
            />
            <ChecklistItem
              text="Menggunakan huruf besar dan huruf kecil"
              isError={checklistPasswordErrors.get("alphabet") ? true : false}
            />
            <ChecklistItem
              text="Gunakan minimal 1 simbol dan 1 angka"
              isError={
                checklistPasswordErrors.get("symbolNumeric") ? true : false
              }
            />
          </Box>
        )}
        <InputPassword
          data-testid="input_passwordConfirmation"
          label="Ketik ulang password"
          name="retype-password"
          value={retypePassword}
          errorMessage={errors.get("retypePassword")}
          onChange={(e: React.FormEvent<HTMLInputElement>) =>
            handleValidatePassword(e, 2000)
          }
          onBlur={(e: React.FormEvent<HTMLInputElement>) =>
            handleValidatePassword(e)
          }
          autoComplete="new-password"
        />
        <Spacer mb="16px" />
        <Box
          data-testid="input_recaptcha"
          className="form_group_recaptcha"
          mb="14px"
        >
          <ReCAPTCHA
            sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY || ""}
            onChange={onVerifyRecaptcha}
            onExpired={onExpiredRecaptcha}
            ref={recaptchaRef}
            size="normal"
          />
        </Box>
        <Flex alignItems={"center"} mb="16px">
          <Checkbox
            data-tetid="checkbox_terms"
            isChecked={isTermsApproved}
            onChange={setIsTermsApproved.toggle}
          >
            <Body
              variant="Default14"
              color="shade04"
              display={"flex"}
              alignItems={"center"}
            >
              Saya menyetujui&nbsp;
            </Body>
          </Checkbox>
          <Body
            onClick={onOpen}
            color={colors.accent03}
            variant="Bold12"
            fontSize={"sm"}
            cursor={"pointer"}
          >
            Kebijakan Privasi IDN
          </Body>
        </Flex>
        <ButtonPrimary
          data-testid="button_submitRegister"
          width="100%"
          mb="16px"
          type="submit"
          isLoading={loadingButton}
          loadingText="Daftar"
          isDisabled={
            !isTermsApproved ||
            isButtonSubmitDisabled ||
            !isRecaptchaChecked ||
            !tokenRecaptcha
          }
        >
          Daftar
        </ButtonPrimary>
      </form>
      <Body
        data-testid="paragraph_login"
        variant="Default14"
        color="shade04"
        textAlign="center"
      >
        Sudah punya akun?&nbsp;
        <Link data-testid="link_login" href="/login">
          Masuk
        </Link>
      </Body>
      <Modal
        onClose={onClose}
        isOpen={isOpen}
        noMaxHeight={true}
        disabled={true}
        maxWidth={"400px"}
      >
        <ModalHeader showed={false} />
        <Body
          variant="Bold16"
          color="shade01"
          display={"flex"}
          alignItems={"center"}
          mb={4}
        >
          Kebijakan Privasi&nbsp;
          <Body
            onClick={onOpen}
            color={colors.red03}
            variant="Bold12"
            fontSize={"md"}
          >
            IDN Connect
          </Body>
        </Body>
        <div
          data-testid="modal_successRegistration"
          className="mx-auto flex flex-col"
        >
          <Body variant="Default14" color="shade03">
            Terima kasih telah menggunakan layanan IDN Connect. Kami menghargai
            kepercayaan Anda dan berkomitmen untuk melindungi informasi pribadi
            Anda. Kebijakan Privasi ini menjelaskan bagaimana kami mengumpulkan,
            menggunakan, menyimpan, dan melindungi data pribadi Anda.
          </Body>
          <Accordion
            allowToggle
            border="1px solid #E5E9EC"
            borderRadius="16px"
            my={4}
            zIndex={999}
          >
            {accordionContent.map((item, index) => (
              <AccordionItem
                key={index}
                bg="white"
                boxShadow="sm"
                border={"none"}
                borderRadius={"16px"}
              >
                <h2>
                  <AccordionButton
                    py={4}
                    px={4}
                    _hover={{ bg: "none", boxShadow: "none" }}
                  >
                    <Box
                      as="span"
                      flex="1"
                      textAlign="left"
                      fontWeight="semibold"
                      fontSize="sm"
                    >
                      {item.title}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel
                  pb={4}
                  px={4}
                  pt={0}
                  fontSize="sm"
                  color="gray.600"
                >
                  {item.content}
                </AccordionPanel>
                <Divider color={"shade06"} />
              </AccordionItem>
            ))}
          </Accordion>
          <Box bg={"shade08"} p={2} rounded={"8px"} mb={4}>
            <Flex alignItems={"start"} gap={2}>
              <Box>
                <Headset fill={colors.red03} />
              </Box>
              <Box>
                <Body variant="SmallBold" color="shade02">
                  Kontak Kami
                </Body>
                <Body variant="Default14" color="shade03">
                  Jika Anda memiliki pertanyaan atau permintaan terkait data
                  pribadi Anda, silakan hubungi kami di:
                  <Body variant="Bold14" color="red03">
                    support@idn.media
                  </Body>
                </Body>
              </Box>
            </Flex>
          </Box>
          <Button
            data-testid="button_linkToIndex"
            text={"Saya Mengerti"}
            onClick={handleAgreePolicy}
            width="full-width"
          />
        </div>
      </Modal>
    </Layout>
  );
};

export default Register;
